import React, { useRef, type PropsWithChildren } from "react";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText, {
  type DialogContentTextProps,
} from "@material-ui/core/DialogContentText";
import DialogTitle, {
  type DialogTitleProps,
} from "@material-ui/core/DialogTitle";
import Button, { type ButtonProps } from "@material-ui/core/Button";

import Dialog, { type DialogProps } from "src/components/Dialog";
import { StatefulLoadableButton } from "src/components/LoadableButton";

// fix-vim-higlight = }

export interface ConfirmDialogProps {
  open: DialogProps["open"];
  title: DialogTitleProps["children"];
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
  text: DialogContentTextProps["children"];
  cancel?: ButtonProps["children"];
  confirm?: ButtonProps["children"];
}

type ConfirmDialogPropsWithChildren = PropsWithChildren<ConfirmDialogProps>;

type ConfirmDialogContentProps = Omit<
  ConfirmDialogPropsWithChildren,
  "open" | "title"
>;

export function ConfirmDialogContent({
  text,
  onCancel,
  onConfirm,
  cancel = "No",
  confirm,
  children,
}: ConfirmDialogContentProps) {
  return (
    <>
      <DialogContent>
        {text ? <DialogContentText>{text}</DialogContentText> : null}
      </DialogContent>
      {children}
      <DialogActions>
        <Button onClick={onCancel}>{cancel}</Button>
        <StatefulLoadableButton
          onClick={async () => {
            await onConfirm();
          }}
          color="primary"
          variant="contained"
        >
          {confirm || "Yes"}
        </StatefulLoadableButton>
      </DialogActions>
    </>
  );
}

type MemorizedContentProps = {
  title: ConfirmDialogProps["title"];
} & ConfirmDialogContentProps;

function ConfirmDialog({
  open,
  title,
  onCancel,
  ...rest
}: ConfirmDialogPropsWithChildren) {
  // Keep last open-frame content during the close transition so labels/children
  // don't flash when the parent clears pending state (set to null/undefined).
  const propsRef = useRef<MemorizedContentProps>({ title, onCancel, ...rest });
  if (open) propsRef.current = { title, onCancel, ...rest };

  const { title: titleMemory, ...contentMemory } = propsRef.current;

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{titleMemory}</DialogTitle>
      <ConfirmDialogContent {...contentMemory} onCancel={onCancel} />
    </Dialog>
  );
}

export default ConfirmDialog;
